import React from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollTop } from 'Core/hooks/useScrollTop';
import Page from 'Core/components/Page';
import Card from 'Core/components/FormElements/Card';
import { FaPhoneAlt, FaExclamationTriangle, FaBan, FaRedo } from 'react-icons/fa';

const PhoneCard = ({ title, phone, description }) => (
  <a
    href={`tel:${phone}`}
    className="block p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 hover:shadow-md transition-all duration-200 group"
  >
    <div className="flex items-center gap-3">
      <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center group-hover:bg-blue-100 transition-colors">
        <FaPhoneAlt className="text-blue-600" />
      </div>
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-blue-600 font-semibold">{phone}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  </a>
);

const InfoCard = ({
  icon: Icon,
  title,
  description,
  iconBgColor = 'bg-amber-50',
  iconColor = 'text-amber-600',
}) => (
  <div className="block p-4 bg-slate-50 border border-slate-200 rounded-lg">
    <div className="flex items-center gap-3">
      <div
        className={`flex-shrink-0 w-10 h-10 ${iconBgColor} rounded-full flex items-center justify-center`}
      >
        <Icon className={iconColor} />
      </div>
      <div>
        <p className="font-medium text-gray-900">{title}</p>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
  </div>
);

export default function FinancingVerificationRequired() {
  const { t } = useTranslation();
  document.title = t('patientfi.verificationRequired.pageTitle');
  useScrollTop();

  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="patientfi"
    >
      <div className="max-w-4xl mx-auto mt-6">
        {/* Header Card */}
        <Card className="!p-0 overflow-hidden" cardPadding="none">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <FaExclamationTriangle className="text-white text-xl" />
              </div>
              <div>
                <h1
                  className="text-xl font-semibold text-white"
                  data-testid="title-verification-required"
                >
                  {t('patientfi.verificationRequired.title')}
                </h1>
                <p className="text-amber-100 text-sm">
                  {t('patientfi.verificationRequired.subtitle')}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-6">{t('patientfi.verificationRequired.description')}</p>

            {/* Step 1: Contact PatientFi */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </span>
                <span className="font-medium text-gray-700">
                  {t('patientfi.verificationRequired.step1Instruction')}
                </span>
              </div>
              <PhoneCard
                title={t('patientfi.requalify.patientFiTitle')}
                phone={t('patientfi.requalify.phoneNumbers.patientFi')}
                description={t('patientfi.verificationRequired.patientFiDescription')}
              />
            </div>

            {/* Step 2: Disable PatientFi Transaction */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </span>
                <span className="font-medium text-gray-700">
                  {t('patientfi.verificationRequired.step2Instruction')}
                </span>
              </div>
              <InfoCard
                icon={FaBan}
                title={t('patientfi.verificationRequired.disableTransactionTitle')}
                description={t('patientfi.verificationRequired.disableTransactionDescription')}
                iconBgColor="bg-red-50"
                iconColor="text-red-600"
              />
            </div>

            {/* Step 3: Start New Application */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-6 h-6 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </span>
                <span className="font-medium text-gray-700">
                  {t('patientfi.verificationRequired.step3Instruction')}
                </span>
              </div>
              <InfoCard
                icon={FaRedo}
                title={t('patientfi.verificationRequired.newApplicationTitle')}
                description={t('patientfi.verificationRequired.newApplicationDescription')}
                iconBgColor="bg-green-50"
                iconColor="text-green-600"
              />
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
